import { createStore, applyMiddleware, compose, combineReducers } from "redux";
import { thunk } from "redux-thunk";
import type { ThunkAction, ThunkDispatch } from "redux-thunk";
import { changeLoginState } from "./reducers/authenticationReducer";
import { changeModalVisibility } from "./reducers/modalReducer";
import { handleTaskActions } from "./reducers/taskReducer";
import { handleLayoutActions } from "./reducers/layoutReducer";
import { handleUserDetailsActions } from "./reducers/userReducer";
import { handleGlobalActions } from "./reducers/globalReducer";
import { verifyAuthentication } from "./actions/AuthenticationActions";
import { handleFeedbackActions } from "./reducers/feedbackReducer";
import { handleStyleActions } from "./reducers/StyleReducer";
import { useAppSelector } from "./hooks";
import type { AuthAction } from "./reducers/authenticationReducer";
import type { FeedbackAction } from "./reducers/feedbackReducer";
import type { GlobalAction } from "./reducers/globalReducer";
import type { LayoutAction } from "./reducers/layoutReducer";
import type { ModalAction } from "./reducers/modalReducer";
import type { StyleAction } from "./reducers/StyleReducer";
import type { TaskAction } from "./reducers/taskReducer";
import type { UserAction } from "./reducers/userReducer";

// Adding a new reducer? Save yourself some headache and add it to the
// renderWithRedux test helper as well.
const rootReducer = combineReducers({
  login: changeLoginState,
  modal: changeModalVisibility,
  task: handleTaskActions,
  layouts: handleLayoutActions,
  userDetails: handleUserDetailsActions,
  global: handleGlobalActions,
  feedback: handleFeedbackActions,
  styles: handleStyleActions
});

/** Shape of the whole redux store; use with useAppSelector. */
export type RootState = ReturnType<typeof rootReducer>;

/** Any plain action this app dispatches, as a union of the per-slice actions. */
export type AppAction =
  | AuthAction
  | FeedbackAction
  | GlobalAction
  | LayoutAction
  | ModalAction
  | StyleAction
  | TaskAction
  | UserAction;

/**
 * Dispatch that also accepts the thunks in src/actions. The store applies the
 * thunk middleware without an extra argument, so thunks receive `undefined`.
 */
export type AppDispatch = ThunkDispatch<RootState, undefined, AppAction>;

/** Return type for the thunks in src/actions. */
export type AppThunk<ReturnType = void> = ThunkAction<
  ReturnType,
  RootState,
  undefined,
  AppAction
>;

const initialState = {};

// Saves the values from the redux state into localstorage for some db hit improvements
const saveState = (state: RootState) => {
  try {
    const serializedState = JSON.stringify(state);
    localStorage.setItem("dashboi", serializedState);
  } catch (err) {
    // Persistence is best-effort; a full quota should not break the app.
  }
};

// loads state from local storage and uses initial state
const loadState = () => {
  try {
    const serializedState = localStorage.getItem("dashboi");
    if (serializedState === null) {
      return initialState;
    }
    return JSON.parse(serializedState);
  } catch (err) {
    return initialState;
  }
};

declare global {
  interface Window {
    __REDUX_DEVTOOLS_EXTENSION_COMPOSE__?: typeof compose;
    /** Injected by the Google Analytics snippet in index.html. */
    gtag?: (
      command: "event",
      action: string,
      params: { event_category: string; event_label: string; value: number }
    ) => void;
  }
}

const composeEnhancers = window.__REDUX_DEVTOOLS_EXTENSION_COMPOSE__ || compose;

const store = createStore(
  rootReducer,
  loadState(),
  composeEnhancers(applyMiddleware(thunk))
);

// on page load, check for authentication results from firebase
store.dispatch(verifyAuthentication() as never);

// whenever changes are made to the global store, replicate them in the browser local storage
store.subscribe(() => {
  saveState(store.getState());
});

export default store;
